import { TaskStatus } from 'constants/tasks';

import { SignerStepKey } from './components/TaskStepCompleteForm/TaskStepCompleteForm.constants';

// Signing renders the real agreements, so it gets a full-screen modal rather than the 500px submit
// drawer the other task kinds share: a signer cannot read what they are about to sign at drawer width.
export const SIGNING_MODAL_ID = 'company-manager-task-signing-modal';

// Compensation Plans can carry a term group per licensed state, which overwhelms a 500px drawer, so
// the signer completes it in a full-screen modal with the state groups tiled.
export const COMP_PLANS_MODAL_ID = 'company-manager-task-comp-plans-modal';

// A Questionnaire runs to dozens of checkbox items with explanations, which a 500px drawer shows only a
// slice of, so the signer reviews it full-screen with the sections tiled.
export const QUESTIONNAIRE_MODAL_ID = 'company-manager-task-questionnaire-modal';

export const TasksTabTestIds = {
  TAB: 'company-manager-tasks-tab',
  FILTER_PILLS: 'company-manager-tasks-filter-pills',
  TABLE: 'company-manager-tasks-table',
  EMPTY: 'company-manager-tasks-empty',
  SUBMIT_DRAWER: 'company-manager-task-submit-drawer',
  REVIEW_DRAWER: 'company-manager-task-review-drawer',
  REVIEW_REASON_INPUT: 'company-manager-task-review-reason',
  ACTION_REVIEW_BUTTON: 'task-action.review-button',
  ACTION_SUBMIT_BUTTON: 'task-action.submit-button',
  ACTION_RESUBMIT_BUTTON: 'task-action.resubmit-button',
  SUBMIT_DRAWER_CLOSE_BUTTON: 'task-submit.close-button',
  SUBMIT_DRAWER_DONE_BUTTON: 'task-submit.done-button',
  SUBMIT_DRAWER_SURFACE_CLOSE_BUTTON: 'task-submit.surface-close-button',
  SUBMIT_DRAWER_CANCEL_BUTTON: 'task-submit.cancel-button',
  SUBMIT_DRAWER_SUBMIT_BUTTON: 'task-submit.submit-button',
  SUBMIT_DRAWER_ERROR: 'task-submit.error-message',
  SUBMIT_DRAWER_UNSUPPORTED: 'task-submit.unsupported-message',
  // Renders only inside the signing modal, so it doubles as that modal's presence selector.
  SIGNING_MODAL_CLOSE_BUTTON: 'task-signing.close-button',
  COMP_PLANS_MODAL_CLOSE_BUTTON: 'task-comp-plans.close-button',
  QUESTIONNAIRE_MODAL_CLOSE_BUTTON: 'task-questionnaire.close-button',
};

// Signer steps completed in the full-screen modal rather than the submit drawer.
export const FULL_SCREEN_SIGNER_STEP_MODALS = {
  [SignerStepKey.COMPENSATION_PLANS]: {
    modalId: COMP_PLANS_MODAL_ID,
    closeButtonDataTestId: TasksTabTestIds.COMP_PLANS_MODAL_CLOSE_BUTTON,
  },
  [SignerStepKey.QUESTIONNAIRE]: {
    modalId: QUESTIONNAIRE_MODAL_ID,
    closeButtonDataTestId: TasksTabTestIds.QUESTIONNAIRE_MODAL_CLOSE_BUTTON,
  },
};

export const TaskFilterKey = {
  NEW: TaskStatus.NEW,
  SUBMITTED: TaskStatus.SUBMITTED,
  COMPLETED: TaskStatus.APPROVED,
  REJECTED: TaskStatus.REJECTED,
};

export const TaskStatusPillLabel = {
  [TaskStatus.NEW]: 'New',
  [TaskStatus.SUBMITTED]: 'Submitted',
  [TaskStatus.APPROVED]: 'Completed',
  [TaskStatus.REJECTED]: 'Rejected',
  [TaskStatus.RETURNED]: 'Returned',
};

export const TaskColumnId = {
  NAME: 'name',
  ASSIGNED_TO: 'assignedTo',
  DUE: 'due',
  LAST_MODIFIED: 'lastModified',
  STATUS: 'status',
  ACTION: 'action',
};

export const TaskDueTone = {
  NEUTRAL: 'neutral',
  DUE_SOON: 'dueSoon',
  OVERDUE: 'overdue',
};

export const TaskSubmitKind = {
  DOCUMENT: 'document',
  ACKNOWLEDGMENT: 'acknowledgment',
  SIGNING: 'signing',
  STEP_COMPLETE: 'stepComplete',
  UNSUPPORTED: 'unsupported',
  RETURNED: 'returned',
};

export const TASK_SIGNATURE_REQUIRED_DETAIL = 'Your signature is required.';

export const TASK_DUE_SOON_DAYS = 14;
export const TASK_OVERDUE_DAYS = 30;
export const TASK_RELATIVE_DAYS_THRESHOLD = 7;

export const TASK_ACCEPTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

export const TASKS_EMPTY_MESSAGE = 'No tasks in this view.';
export const TASKS_TAB_TITLE = 'Tasks';
export const TASKS_TAB_SUBTITLE =
  'Lender-assigned items — appear any time, independent of certification.';

export const TASK_REFERENCE_PREFIX = 'TK-';
export const TASK_REFERENCE_ID_RADIX = 36;

// SUB_COMPANY-scoped tasks are actioned only by users holding the APPROVE_REJECT_CANCEL_TASK
// claim (= company admins), so the assignee reads "Company admin", not "Company-wide".
export const COMPANY_ADMIN_ASSIGNEE_LABEL = 'Company admin';
export const AUTHORIZED_SIGNER_ASSIGNEE_LABEL = 'Authorized Signer';
export const CURRENT_USER_ASSIGNEE_SUFFIX = 'You';

// The signing task shares a generic TaskType name; render this in the Task column so it reads
// as an explicit sign-documents action (the step-completion tasks get the SignerStepTaskName below).
export const SIGNING_TASK_DISPLAY_NAME = 'Authorized Signer — Sign Documents';

// The deferred signer step-completion tasks all share one TaskType (so task.name is generic);
// the wizard step they defer (metadata.stepKey) is what distinguishes them in the Task column.
export const SignerStepTaskName = {
  Attestation: 'Authorized Signer — Attestation',
  CompensationPlans: 'Authorized Signer — Compensation Plans',
  Questionnaire: 'Authorized Signer — Review Questionnaire',
};

export const QUESTIONNAIRE_TASK_TITLE_PREFIX = 'Review Questionnaire';

// The BE falls back to the step registry's default title when the admin left the step untitled;
// suffixing that onto "Review Questionnaire" would just repeat the word.
export const DEFAULT_QUESTIONNAIRE_STEP_TITLE = 'Questionnaire';

export const TASK_ACTION_SUBMIT_LABEL = 'Submit';
export const TASK_ACTION_SIGN_LABEL = 'Sign';
export const TASK_ACTION_VIEW_LABEL = 'View';
